import { useEffect, useRef } from "react";

const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";

function ensureTurnstileScript(onLoad) {
  if (typeof window === "undefined") return;
  if (window.turnstile) {
    onLoad();
    return;
  }
  let script = document.getElementById(TURNSTILE_SCRIPT_ID);
  if (!script) {
    script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
  if (script.dataset.loaded === "true") {
    onLoad();
    return;
  }
  if (script.readyState === "complete") {
    onLoad();
    return;
  }
  const markLoadedAndRun = () => {
    script.dataset.loaded = "true";
    onLoad();
  };
  script.addEventListener("load", markLoadedAndRun, { once: true });
}

export default function TurnstileWidget({ siteKey, onToken, onExpire, className = "" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const tokenCbNameRef = useRef(`obihubTurnstileToken_${Math.random().toString(36).slice(2)}`);
  const expireCbNameRef = useRef(`obihubTurnstileExpire_${Math.random().toString(36).slice(2)}`);
  const errorCbNameRef = useRef(`obihubTurnstileError_${Math.random().toString(36).slice(2)}`);
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    let cancelled = false;
    let retries = 0;
    let retryTimer = null;

    window[tokenCbNameRef.current] = (token) => onTokenRef.current?.(String(token || ""));
    window[expireCbNameRef.current] = () => onExpireRef.current?.();
    window[errorCbNameRef.current] = () => onExpireRef.current?.();

    const renderWidget = () => {
      if (cancelled || widgetIdRef.current !== null) return;
      if (!containerRef.current || !siteKey) return;
      if (!window.turnstile) {
        retries += 1;
        if (retries <= 60) {
          retryTimer = window.setTimeout(renderWidget, 250);
        } else {
          onExpireRef.current?.();
        }
        return;
      }
      try {
        containerRef.current.innerHTML = "";
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: window[tokenCbNameRef.current],
          "expired-callback": window[expireCbNameRef.current],
          "error-callback": window[errorCbNameRef.current],
          retry: "auto",
          "refresh-expired": "auto",
          theme: "light",
          size:
            window.matchMedia && window.matchMedia("(max-width: 420px)").matches
              ? "compact"
              : "normal",
        });
      } catch {
        onExpireRef.current?.();
        widgetIdRef.current = null;
      }
    };

    ensureTurnstileScript(renderWidget);
    renderWidget();

    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
      try {
        delete window[tokenCbNameRef.current];
        delete window[expireCbNameRef.current];
        delete window[errorCbNameRef.current];
      } catch {}
    };
  }, [siteKey]);

  return (
    <div className={className}>
      <div ref={containerRef} />
    </div>
  );
}
